import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Coffee, Package, TrendingUp, DollarSign, Plus, Search, Edit2, Trash2, X, Save, Loader2, AlertCircle, Check } from 'lucide-react';
import cafeService from '../services/cafe';

const formatRupiah = (num) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(num || 0);

const KATEGORI_PRODUK = ['MAKANAN', 'MINUMAN', 'SNACK', 'LAINNYA'];

const CafeDashboard = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterKategori, setFilterKategori] = useState('All');
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({ nama: '', kategori: 'MINUMAN', harga: '', stok: '' });
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState({ totalProducts: 0, totalMenuValue: 0 });

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (filterKategori !== 'All') params.kategori = filterKategori;
      const data = await cafeService.getProducts(params);
      setProducts(data.products || []);
      const dash = await cafeService.getDashboard();
      setStats(dash);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [searchTerm, filterKategori]);

  const showSuccess = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 3000);
  };

  const openAddForm = () => {
    setEditingProduct(null);
    setFormData({ nama: '', kategori: 'MINUMAN', harga: '', stok: '' });
    setShowForm(true);
  };

  const openEditForm = (product) => {
    setEditingProduct(product);
    setFormData({
      nama: product.nama,
      kategori: product.kategori,
      harga: product.harga,
      stok: product.stok
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nama || !formData.harga) {
      setError('Nama dan harga wajib diisi');
      return;
    }
    setSaving(true);
    try {
      if (editingProduct) {
        await cafeService.updateProduct(editingProduct._id, formData);
        showSuccess('Produk berhasil diupdate');
      } else {
        await cafeService.createProduct(formData);
        showSuccess('Produk berhasil ditambahkan');
      }
      setShowForm(false);
      setEditingProduct(null);
      await fetchData();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`Nonaktifkan produk ${product.nama}?`)) return;
    try {
      await cafeService.deleteProduct(product._id);
      showSuccess('Produk dinonaktifkan');
      await fetchData();
    } catch (err) {
      setError(err.message);
    }
  };

  const cards = [
    { title: 'Total Produk', value: stats.totalProducts, icon: Package, color: 'bg-blue-50', iconColor: 'text-blue-600' },
    { title: 'Total Menu', value: `${products.length} items`, icon: Coffee, color: 'bg-amber-50', iconColor: 'text-amber-600' },
    { title: 'Nilai Stok', value: formatRupiah(stats.totalMenuValue), icon: DollarSign, color: 'bg-green-50', iconColor: 'text-green-600' },
  ];

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">POS Cafe</h2>
          <p className="mt-1 text-sm text-gray-500">Manajemen produk dan menu cafe</p>
        </div>
        <button onClick={openAddForm} className="btn-primary text-sm">
          <Plus className="w-4 h-4" /><span>Tambah Produk</span>
        </button>
      </div>

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 animate-slide-up">
          <div className="flex items-center space-x-2"><Check className="w-5 h-5 text-green-500" /><p className="text-sm text-green-800">{success}</p></div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-center space-x-2"><AlertCircle className="w-5 h-5 text-red-500" /><p className="text-sm text-red-700">{error}</p></div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {cards.map((card, i) => (
          <div key={card.title} className="card p-4 animate-slide-up" style={{ animationDelay: `${i * 50}ms` }}>
            <div className="flex items-center space-x-3">
              <div className={`p-2.5 rounded-xl ${card.color}`}><card.icon className={`w-5 h-5 ${card.iconColor}`} /></div>
              <div><p className="text-xs text-gray-500">{card.title}</p><p className="text-xl font-bold text-gray-900">{card.value}</p></div>
            </div>
          </div>
        ))}
      </div>

      {/* Product Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 overflow-y-auto animate-fade-in" onClick={() => { if (!saving) setShowForm(false); }}>
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20">
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-auto z-10 animate-scale-in p-6" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center space-x-3">
                  <div className="p-2 rounded-xl bg-amber-50"><Coffee className="w-5 h-5 text-amber-600" /></div>
                  <h3 className="text-lg font-bold text-gray-900">{editingProduct ? 'Edit Produk' : 'Tambah Produk'}</h3>
                </div>
                <button onClick={() => setShowForm(false)} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg"><X className="w-5 h-5" /></button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nama Produk <span className="text-red-500">*</span></label>
                  <input type="text" value={formData.nama} onChange={e => setFormData(p => ({ ...p, nama: e.target.value }))}
                    className="input-field w-full" placeholder="Nama produk..." />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Kategori</label>
                    <select value={formData.kategori} onChange={e => setFormData(p => ({ ...p, kategori: e.target.value }))} className="select-field w-full">
                      {KATEGORI_PRODUK.map(k => <option key={k} value={k}>{k}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Harga (Rp) <span className="text-red-500">*</span></label>
                    <input type="number" value={formData.harga} onChange={e => setFormData(p => ({ ...p, harga: e.target.value }))}
                      className="input-field w-full" placeholder="0" min="0" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Stok</label>
                  <input type="number" value={formData.stok} onChange={e => setFormData(p => ({ ...p, stok: e.target.value }))}
                    className="input-field w-full" placeholder="0" min="0" />
                </div>
                <div className="flex justify-end space-x-3 pt-2">
                  <button type="button" onClick={() => setShowForm(false)} disabled={saving} className="btn-secondary text-sm">
                    <X className="w-4 h-4" /><span>Batal</span>
                  </button>
                  <button type="submit" disabled={saving} className="btn-primary text-sm">
                    {saving ? <><Loader2 className="w-4 h-4 animate-spin" /><span>Menyimpan...</span></>
                      : <><Save className="w-4 h-4" /><span>{editingProduct ? 'Update' : 'Simpan'}</span></>}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Product List */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center justify-between w-full">
            <h3 className="section-title">Daftar Produk</h3>
            <div className="flex items-center space-x-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input type="text" placeholder="Cari produk..." value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 bg-gray-50 hover:bg-white focus:bg-white w-48" />
              </div>
              <select value={filterKategori} onChange={e => setFilterKategori(e.target.value)} className="select-field text-sm">
                <option value="All">Semua</option>
                {KATEGORI_PRODUK.map(k => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>
          </div>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="flex flex-col items-center py-16"><Loader2 className="h-8 w-8 animate-spin text-amber-500" /><span className="mt-3 text-gray-500">Loading...</span></div>
          ) : products.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-xs text-gray-500 uppercase tracking-wider">
                    <th className="pb-3 font-semibold">Kode</th>
                    <th className="pb-3 font-semibold">Nama Produk</th>
                    <th className="pb-3 font-semibold">Kategori</th>
                    <th className="pb-3 font-semibold text-right">Harga</th>
                    <th className="pb-3 font-semibold text-right">Stok</th>
                    <th className="pb-3 font-semibold text-right">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p, idx) => (
                    <tr key={p._id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors animate-slide-up" style={{ animationDelay: `${idx * 30}ms` }}>
                      <td className="py-3 pr-4">
                        <span className="text-xs font-mono text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{p.kode}</span>
                      </td>
                      <td className="py-3 pr-4 font-semibold text-gray-900">{p.nama}</td>
                      <td className="py-3 pr-4">
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-purple-100 text-purple-700">{p.kategori}</span>
                      </td>
                      <td className="py-3 pr-4 text-right font-bold text-gray-900">{formatRupiah(p.harga)}</td>
                      <td className="py-3 pr-4 text-right">
                        <span className={`font-semibold ${p.stok > 0 ? 'text-green-600' : 'text-red-500'}`}>{p.stok}</span>
                      </td>
                      <td className="py-3 text-right">
                        <div className="flex space-x-1 justify-end">
                          <button onClick={() => openEditForm(p)} className="p-1.5 text-gray-400 hover:text-indigo-600 rounded-lg" title="Edit">
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => handleDelete(p)} className="p-1.5 text-gray-400 hover:text-red-600 rounded-lg" title="Nonaktifkan">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <Coffee className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">Belum ada produk</p>
              <p className="text-gray-400 text-sm mt-1">Tambahkan produk cafe terlebih dahulu</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CafeDashboard;
